import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { contentAPI, profilesAPI, publishingAPI } from '../services/api';
import { useToast } from './Toast';
import '../styles/WorkspaceChrome.css';
import './InProgress.css';

function PublishingQueue() {
  const { showToast } = useToast();
  const [drafts, setDrafts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wpConnected, setWpConnected] = useState(false);
  const [pushingId, setPushingId] = useState(null);
  const [markingId, setMarkingId] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { workflow_kind: 'human_research' };
      if (selectedProfile) params.profile_id = selectedProfile;
      const [draftsRes, profilesRes, connRes] = await Promise.all([
        contentAPI.getDrafts(params),
        profilesAPI.getProfiles(),
        publishingAPI.getWordPressConnection().catch(() => ({ data: { connection: null } })),
      ]);
      setDrafts(draftsRes.data);
      setProfiles(profilesRes.data);
      setWpConnected(!!connRes.data?.connection?.api_key_set);
    } catch (err) {
      console.error('Error loading publishing queue:', err);
      setError(
        err.response?.data?.error || err.message || 'Failed to load publishing queue'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [selectedProfile]);

  const readyDrafts = useMemo(
    () =>
      drafts.filter((d) => d.status === 'approved' || d.status === 'published'),
    [drafts]
  );

  const handlePushWordPress = async (draftId) => {
    try {
      setPushingId(draftId);
      const res = await contentAPI.pushDraftToWordPress(draftId);
      const created = res.data?.wordpress?.created;
      showToast(
        created ? 'Pushed to WordPress as a new draft.' : 'Updated WordPress draft.',
        'success'
      );
      await loadData();
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Push to WordPress failed',
        'error'
      );
    } finally {
      setPushingId(null);
    }
  };

  const handleMarkPublished = async (draftId) => {
    try {
      setMarkingId(draftId);
      await contentAPI.markDraftPublishedManually(draftId);
      showToast('Marked as published.', 'success');
      await loadData();
    } catch (err) {
      showToast(
        err.response?.data?.error || err.message || 'Update failed',
        'error'
      );
    } finally {
      setMarkingId(null);
    }
  };

  if (loading) {
    return (
      <div className="in-progress pf-explore publishing-queue-page">
        <div className="pf-loading">Loading publishing queue…</div>
      </div>
    );
  }

  return (
    <div className="in-progress pf-explore publishing-queue-page">
      <div className="pf-header">
        <div>
          <h2>Publish</h2>
          <p className="pf-sub">
            Approved pieces ready for handoff. Open the publish checklist beside your CMS, push to
            WordPress as a draft, or mark published when live elsewhere.
          </p>
        </div>
        <div className="pf-filters">
          <select
            value={selectedProfile}
            onChange={(e) => setSelectedProfile(e.target.value)}
            className="pf-filter-select"
            aria-label="Filter by topic profile"
          >
            <option value="">All topic profiles</option>
            {profiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!wpConnected && (
        <p className="pf-callout publishing-banner">
          Connect WordPress under{' '}
          <Link to="/account">Account & publishing</Link> to push drafts. You can still mark articles
          published manually.
        </p>
      )}

      {error && <div className="pf-error-banner">{error}</div>}

      <div className="drafts-list research-briefs-fullwidth pf-list-card">
        <h3>Publishing queue ({readyDrafts.length})</h3>
        {readyDrafts.length === 0 ? (
          <div className="pf-empty">
            <p>No articles in the queue yet.</p>
            <p className="pf-empty-hint">
              From a draft workspace, use <strong>Send to publishing queue</strong> when the article
              is ready. It will appear here for WordPress push or manual publish.
            </p>
          </div>
        ) : (
          <div className="drafts-items">
            {readyDrafts.map((draft) => (
              <div key={draft.id} className="draft-item research-brief-row">
                <div className="draft-item-content">
                  <h4>{draft.title}</h4>
                  <div className="draft-item-meta">
                    {draft.profile_name && (
                      <span className="meta-badge">{draft.profile_name}</span>
                    )}
                    <span className={`meta-badge status-${draft.status}`}>
                      {draft.status === 'published' ? 'Published' : 'Approved'}
                    </span>
                    {draft.wp_post_id && draft.wp_status && (
                      <span className="meta-badge status-wp">
                        WP: {draft.wp_status}
                      </span>
                    )}
                  </div>
                  {draft.wp_edit_link && (
                    <p className="publishing-wp-link-row">
                      <a
                        href={draft.wp_edit_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="publishing-wp-edit-link"
                      >
                        Edit in WordPress
                      </a>
                    </p>
                  )}
                  <div className="research-brief-row-actions">
                    <Link
                      to={`/publish-preflight/${draft.id}`}
                      className="pf-btn pf-btn-primary"
                    >
                      Open publish checklist
                    </Link>
                    <Link
                      to={`/research-brief/${draft.id}`}
                      className="pf-btn pf-btn-secondary"
                    >
                      Open workspace
                    </Link>
                    {draft.status === 'approved' && (
                      <>
                        <button
                          type="button"
                          className="pf-btn pf-btn-secondary"
                          disabled={!wpConnected || pushingId === draft.id}
                          onClick={() => void handlePushWordPress(draft.id)}
                        >
                          {pushingId === draft.id
                            ? 'Pushing…'
                            : draft.wp_post_id
                              ? 'Re-push to WordPress'
                              : 'Push to WordPress'}
                        </button>
                        <button
                          type="button"
                          className="pf-btn pf-btn-secondary publishing-mark-btn"
                          disabled={markingId === draft.id}
                          onClick={() => void handleMarkPublished(draft.id)}
                        >
                          {markingId === draft.id ? 'Saving…' : 'Mark published manually'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PublishingQueue;
